import { components } from "@appretio/api";
import type { Option } from "@appretio/ui-kit";
import { SelectGroup } from "@appretio/ui-kit";
import type { ReactNode } from "react";
import React, { useCallback, useMemo } from "react";
import { useBudgetScope } from "../../hooks/useBudgetScope";
import { useFetchVersions } from "../../hooks/useFetchVersions";
import { useScopedBudgetVersions } from "../../hooks/useScopedBudgetVersions";
import { CubeOperation } from "../cubeOperationModal/CubeOperation";
import { ForecastContextDropdown } from "./ForecastContextDropdown";
import { userVersionSlot, versionSlot } from "./ForecastContextSelector.css";

type ForecastSelectionField =
  | "user_version"
  | "reference_users"
  | "budget_version"
  | "forecast_version";

export type ForecastVersionSelections = {
  /** The caller's writable version (the Save target). */
  user_version?: string;
  /**
   * Read-only reference cubes: other users' versions and the caller's
   * own non-writable versions, mixed in one list.
   */
  reference_users?: string[];
  budget_version?: string;
  forecast_version?: string;
};

/**
 * Keys in {@link ForecastVersionSelections} that identify the *writable*
 * scalar cube — the one the Save button targets.
 *
 * ``reference_users`` is excluded because it's a ``string[]`` (multiple
 * read-only reference cubes) and ``forecast_version`` because DS versions
 * are always read-only. Exposing this narrower type to
 * ``useMainPageState`` removes the ``as string | undefined`` cast that
 * would otherwise be needed to bridge the mismatch.
 */
export type SaveCubeKey = "user_version" | "budget_version";

const EMPTY_ARRAY: Option[] = [];

function toCubeVersionOptions(
  versions: components["schemas"]["CubeVersion"][] | null | undefined
): Option[] {
  if (!versions?.length) return EMPTY_ARRAY;

  return versions
    .filter((version) => version.cube_id && version.label)
    .map((version) => ({
      label: version.label,
      value: version.cube_id
    }));
}

interface ForecastContextSelectorProps {
  /**
   * Currently selected versions for forecast context
   */
  selected: ForecastVersionSelections;
  /**
   * Sets selected versions for forecast context
   */
  setSelected: React.Dispatch<React.SetStateAction<ForecastVersionSelections>>;
  /**
   * Optionally pass selected brand to load budget versions related to the brand
   */
  brandSelected?: string;
  /**
   * Optionally pass error state to display error messages
   */
  error?: boolean;
  /**
   * Action icons rendered inside the User Version dropdown trigger (e.g. drop).
   * Ordered by destructiveness: least → most.
   */
  userVersionTriggerActions?: ReactNode;
  /**
   * Content rendered in the User Version dropdown footer (e.g. "New version").
   */
  userVersionFooter?: ReactNode | ((close: () => void) => ReactNode);
  /**
   * Action icons rendered inside the Budget Version dropdown trigger (e.g. lock, drop).
   * Ordered by destructiveness: least → most.
   */
  budgetVersionTriggerActions?: ReactNode;
  /**
   * Content rendered in the Budget Version dropdown footer (e.g. "New version").
   */
  budgetVersionFooter?: ReactNode | ((close: () => void) => ReactNode);
}

export function ForecastContextSelector({
  selected,
  setSelected,
  brandSelected,
  error,
  userVersionTriggerActions,
  userVersionFooter,
  budgetVersionTriggerActions,
  budgetVersionFooter
}: ForecastContextSelectorProps) {
  const { data, isLoading } = useFetchVersions();
  const { data: budgetData } = useScopedBudgetVersions(brandSelected);
  // Only a brand-scoped budget is hidden behind a brand selection; a
  // tenant-wide one has no brand, so an empty list means none exist yet.
  const budgetVersionNoResultsText =
    useBudgetScope() === "brand"
      ? brandSelected
        ? undefined
        : "Choose a brand to view options"
      : "No budget versions yet";

  const userVersionOptions = useMemo(() => {
    // Display-only "(main)" marker — resolveCubeId matches raw labels
    // from the versions payload, not these option labels.
    return toCubeVersionOptions(data?.user_versions).map((option) => {
      const version = data?.user_versions?.find(
        (v) => v.cube_id === option.value
      );
      return version?.is_main
        ? { ...option, label: `${option.label} (main)` }
        : option;
    });
  }, [data?.user_versions]);

  // Own non-writable versions load as read-only references, exactly like
  // other users' cubes — one list, own versions first.
  const referenceOptions = useMemo(() => {
    const ownReferences = (data?.user_versions ?? []).filter(
      (v) => v.cube_id !== selected.user_version
    );
    return [
      ...toCubeVersionOptions(ownReferences),
      ...toCubeVersionOptions(data?.other_user_versions)
    ];
  }, [data?.user_versions, data?.other_user_versions, selected.user_version]);

  const budgetVersionOptions = useMemo(
    () => toCubeVersionOptions(budgetData?.budget_versions),
    [budgetData?.budget_versions]
  );

  const forecastVersionOptions = useMemo(() => {
    const dsVersions = data?.ds_versions ?? [];

    if (!dsVersions.length) return EMPTY_ARRAY;

    return dsVersions.map((version) => ({
      label: version.label,
      value: version.cube_id
    }));
  }, [data?.ds_versions]);

  const handleSetSelected = useCallback(
    (field: ForecastSelectionField, value: string | string[]) => {
      // A cleared single-select reports "", which is no cube.
      const next = value === "" ? undefined : value;
      setSelected((prev) => {
        // A version promoted to writable can't stay a reference too.
        if (field === "user_version" && typeof next === "string") {
          return {
            ...prev,
            user_version: next,
            reference_users: prev.reference_users?.filter((id) => id !== next)
          };
        }
        return { ...prev, [field]: next };
      });
    },
    [setSelected]
  );

  return (
    <SelectGroup>
      <div className={userVersionSlot}>
        <ForecastContextDropdown
          label="User Version"
          options={userVersionOptions}
          selected={selected.user_version}
          loading={isLoading}
          disabled={isLoading}
          error={error}
          onChange={(value) => handleSetSelected("user_version", value)}
          triggerActions={
            <>
              <CubeOperation
                selectedCubeId={selected.user_version}
                label="Inspect context level alignment"
              />
              {userVersionTriggerActions}
            </>
          }
          footerContent={userVersionFooter}
        />
      </div>
      <div className={versionSlot}>
        <ForecastContextDropdown
          label="Reference Cubes"
          options={referenceOptions}
          selected={selected.reference_users}
          loading={isLoading}
          disabled={isLoading}
          multi
          error={error}
          onChange={(value) => handleSetSelected("reference_users", value)}
        />
      </div>
      <div className={versionSlot}>
        <ForecastContextDropdown
          label="Budget Version"
          options={budgetVersionOptions}
          selected={selected.budget_version}
          loading={isLoading}
          disabled={isLoading}
          error={error}
          onChange={(value) => handleSetSelected("budget_version", value)}
          noResultsText={budgetVersionNoResultsText}
          triggerActions={
            <>
              <CubeOperation
                selectedCubeId={selected.budget_version}
                label="Inspect context level alignment"
              />
              {budgetVersionTriggerActions}
            </>
          }
          footerContent={budgetVersionFooter}
        />
      </div>
      <div className={versionSlot}>
        <ForecastContextDropdown
          label="Forecast Version"
          options={forecastVersionOptions}
          selected={selected.forecast_version}
          loading={isLoading}
          disabled={isLoading}
          error={error}
          onChange={(value) => handleSetSelected("forecast_version", value)}
        />
      </div>
    </SelectGroup>
  );
}
